// Inventory toolbar rendered in PixiJS screen-space.
//
// 30 slots in 2 rows of 15, anchored to the bottom-center of the
// viewport. Slots show a colored square for the item and a count in
// the bottom-right corner. Empty slots are dimmed. Hovering a filled
// slot shows the item name in a floating tooltip.
//
// Phase 2 uses solid-color placeholder squares; sprite sheets come later.
// When they do, swap the Graphics fill for a Sprite tinted by items[id].color.

import { Application, Container, Graphics, Text } from "pixi.js";
import { balance } from "./types.ts";
import { getItemDef, items } from "../../shared/items.ts";
import { parseItemId, QUALITY_COLORS, QUALITY_LABELS } from "../../shared/quality.ts";
import type { InventorySlot } from "../../shared/messages.ts";
import { getRecipeByOutputId } from "../../shared/recipes.ts";

const SLOT = balance.inventory.slotSizePx;
const GAP = balance.inventory.slotGapPx;
const COLS = balance.inventory.slotsPerRow;
const ROWS = 2; // contracts:allow-literal — layout constant; always 2 rows per spec
const EMPTY_COLOR = 0x333333;
const EMPTY_ALPHA = balance.inventory.emptySlotAlpha;
const BORDER_COLOR = 0x555555;
const BORDER_WIDTH = 1; // contracts:allow-literal — 1-pixel border
const TOOLBAR_PAD_BOTTOM = 8; // contracts:allow-literal — pixels above bottom edge
const TOOLTIP_OFFSET_Y = -6; // contracts:allow-literal — pixels above top of slot

type SlotUI = {
  container: Container;
  bg: Graphics;
  countText: Text;
  // Phase 7 Block C — "E" badge rendered in the top-left when this slot's
  // rowid matches the equipped weapon's inventoryRowid. Hidden otherwise.
  equippedBadge: Text;
  slotIndex: number;
};

// Callback fired on slot click while selection mode is active. Signals
// which slot the user picked; caller decides whether to mirror the
// selection back via setSelectedSlot() for the visual highlight.
export type InventorySelectionHandler = (slotIndex: number, slot: InventorySlot) => void;

const SELECTION_BORDER = 0xffee88;
const SELECTION_BORDER_W = 2; // contracts:allow-literal — selection highlight stroke width

function getTierLabel(xp: number): string {
  const t = balance.skills.tierThresholds;
  if (xp >= t.master.min) return t.master.label;
  if (xp >= t.expert.min) return t.expert.label;
  if (xp >= t.journeyman.min) return t.journeyman.label;
  if (xp >= t.apprentice.min) return t.apprentice.label;
  return t.novice.label;
}

export class InventoryUI {
  private readonly root: Container;
  private readonly tooltip: Text;
  private readonly slotUIs: SlotUI[] = [];
  private currentSlots: InventorySlot[] = [];
  private selectionHandler: InventorySelectionHandler | null = null;
  private selectedSlotIndex: number | null = null;
  // Phase 7 Block C — getter for the currently-equipped weapon's
  // inventory rowid. Set by main.ts after clientState is available.
  // Returns null when nothing is equipped or the getter is unset.
  private equippedRowidProvider: (() => number | null) | null = null;
  // Injected callback for reading skill XP at tooltip-show time.
  // Set once by main.ts after clientState is available.
  private skillXpProvider: ((skillId: string) => number) | null = null;

  constructor(private readonly app: Application) {
    this.root = new Container();
    app.stage.addChild(this.root);

    this.tooltip = new Text({
      text: "",
      style: {
        fontSize: 12, // contracts:allow-literal — tooltip font size in px
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 2 }, // contracts:allow-literal — outline stroke width
        fontFamily: "monospace",
      },
    });
    this.tooltip.visible = false;
    this.tooltip.zIndex = 100; // contracts:allow-literal — above all other UI
    app.stage.sortableChildren = true;
    app.stage.addChild(this.tooltip);

    this.buildSlots();
    this.layout();
    app.renderer.on("resize", () => this.layout());
  }

  update(slots: InventorySlot[]): void {
    this.currentSlots = slots;
    this.renderSlots();
  }

  setSkillXpProvider(fn: (skillId: string) => number): void {
    this.skillXpProvider = fn;
  }

  setEquippedRowidProvider(fn: () => number | null): void {
    // Don't renderSlots() synchronously here — main.ts wires this before
    // `const clientState = ...` is initialized, and the closure passed in
    // reads clientState.equippedItem; an immediate render would TDZ-throw
    // and block the boot path (Phase 7 Block D — pre-existing Block C
    // bug surfaced once the browser harness's connect timeout was widened).
    // The next inventoryUpdated / equippedItemUpdated will repaint with
    // the now-set provider; the badge is a no-op until first equip anyway.
    this.equippedRowidProvider = fn;
  }

  private buildSlots(): void {
    const total = balance.inventory.maxSlots;
    for (let i = 0; i < total; i++) {
      const col = i % COLS;
      const row = Math.floor(i / COLS);

      const container = new Container();
      container.x = col * (SLOT + GAP);
      container.y = row * (SLOT + GAP);
      container.eventMode = "static";
      container.cursor = "default";

      const bg = new Graphics()
        .roundRect(0, 0, SLOT, SLOT, 3) // contracts:allow-literal — 3px corner radius
        .fill({ color: EMPTY_COLOR, alpha: EMPTY_ALPHA })
        .stroke({ color: BORDER_COLOR, width: BORDER_WIDTH });
      container.addChild(bg);

      const countText = new Text({
        text: "",
        style: {
          fontSize: 10, // contracts:allow-literal — count label font size in px
          fill: 0xffffff,
          stroke: { color: 0x000000, width: 2 }, // contracts:allow-literal — outline stroke width
          fontFamily: "monospace",
        },
      });
      countText.visible = false;
      container.addChild(countText);

      const equippedBadge = new Text({
        text: "E",
        style: {
          fontSize: 10, // contracts:allow-literal — badge font size in px
          fill: 0x88ff88,
          stroke: { color: 0x000000, width: 2 }, // contracts:allow-literal — outline stroke width
          fontFamily: "monospace",
          fontWeight: "bold",
        },
      });
      equippedBadge.x = 2; // contracts:allow-literal — 2px inset from slot's top-left
      equippedBadge.y = 1; // contracts:allow-literal — 1px inset from slot's top-left
      equippedBadge.visible = false;
      container.addChild(equippedBadge);

      container.on("pointerover", (e) => this.showTooltip(i, e.globalX, e.globalY));
      container.on("pointerout", () => this.hideTooltip());
      container.on("pointertap", () => this.handleSlotClick(i));

      this.root.addChild(container);
      this.slotUIs.push({ container, bg, countText, equippedBadge, slotIndex: i });
    }
  }

  // Enable selection-mode: clicks fire the handler. Pass null to
  // disable and clear any highlight. See DECISIONS 2026-04-18
  // "Phase 4 Step 16 inventory selection mode".
  setSelectionMode(handler: InventorySelectionHandler | null): void {
    this.selectionHandler = handler;
    if (handler === null) this.selectedSlotIndex = null;
    this.renderSlots();
  }

  // Caller-driven highlight: lets the market panel echo the current
  // selection back after its own state updates. Passing null clears.
  setSelectedSlot(slotIndex: number | null): void {
    this.selectedSlotIndex = slotIndex;
    this.renderSlots();
  }

  private handleSlotClick(slotIndex: number): void {
    if (!this.selectionHandler) return;
    const slot = this.currentSlots.find((s) => s.slotIndex === slotIndex);
    if (!slot) return; // empty slots aren't selectable for listing
    this.selectionHandler(slotIndex, slot);
  }

  private renderSlots(): void {
    const byIndex = new Map<number, InventorySlot>();
    for (const slot of this.currentSlots) {
      byIndex.set(slot.slotIndex, slot);
    }
    const equippedRowid = this.equippedRowidProvider?.() ?? null;

    for (const ui of this.slotUIs) {
      const slot = byIndex.get(ui.slotIndex);
      ui.bg.clear();
      const isSelected = this.selectedSlotIndex === ui.slotIndex;
      if (slot) {
        const itemDef = getItemDef(slot.itemId) ?? items.copper;
        const { quality } = parseItemId(slot.itemId);
        const borderColor = isSelected
          ? SELECTION_BORDER
          : quality
            ? QUALITY_COLORS[quality]
            : BORDER_COLOR;
        const borderWidth = isSelected ? SELECTION_BORDER_W : quality ? 2 : BORDER_WIDTH; // contracts:allow-literal — 2px quality border vs 1px default
        ui.bg
          .roundRect(0, 0, SLOT, SLOT, 3) // contracts:allow-literal — 3px corner radius
          .fill({ color: itemDef.color })
          .stroke({ color: borderColor, width: borderWidth });
        ui.countText.text = String(slot.count);
        // Anchor count text to bottom-right of the slot.
        ui.countText.x = SLOT - ui.countText.width - 2; // contracts:allow-literal — 2px inset
        ui.countText.y = SLOT - ui.countText.height - 1; // contracts:allow-literal — 1px inset
        ui.countText.visible = true;
        const isEquipped = equippedRowid !== null && slot.rowid === equippedRowid;
        ui.equippedBadge.visible = isEquipped;
      } else {
        ui.bg
          .roundRect(0, 0, SLOT, SLOT, 3) // contracts:allow-literal — 3px corner radius
          .fill({ color: EMPTY_COLOR, alpha: EMPTY_ALPHA })
          .stroke({ color: BORDER_COLOR, width: BORDER_WIDTH });
        ui.countText.visible = false;
        ui.equippedBadge.visible = false;
      }
    }
  }

  private layout(): void {
    const screenW = this.app.renderer.width;
    const screenH = this.app.renderer.height;
    const totalW = COLS * SLOT + (COLS - 1) * GAP;
    const totalH = ROWS * SLOT + (ROWS - 1) * GAP;
    this.root.x = Math.round((screenW - totalW) / 2); // contracts:allow-literal — centering idiom
    this.root.y = screenH - totalH - TOOLBAR_PAD_BOTTOM;
  }

  private showTooltip(slotIndex: number, gx: number, gy: number): void {
    const byIndex = new Map(this.currentSlots.map((s) => [s.slotIndex, s]));
    const slot = byIndex.get(slotIndex);
    if (!slot) return;
    const itemDef = getItemDef(slot.itemId) ?? items.copper;
    const { baseId, quality } = parseItemId(slot.itemId);
    let text = quality ? `${QUALITY_LABELS[quality]} ${itemDef.name}` : itemDef.name;
    // For crafted items with skill gates, show required skill + player's current tier.
    if (this.skillXpProvider) {
      const recipe = getRecipeByOutputId(baseId);
      if (recipe && recipe.skillGates.length > 0) {
        const gateLabels = recipe.skillGates.map((g) => {
          const xp = this.skillXpProvider!(g.skillId);
          return `${g.skillId}: ${getTierLabel(xp)}`;
        });
        text += `\n${gateLabels.join(" | ")}`;
      }
    }
    this.tooltip.text = text;
    this.tooltip.x = gx - this.tooltip.width / 2; // contracts:allow-literal — centering idiom
    this.tooltip.y = gy + TOOLTIP_OFFSET_Y - this.tooltip.height;
    this.tooltip.visible = true;
  }

  private hideTooltip(): void {
    this.tooltip.visible = false;
  }
}
